import Components from "../components";

class ChannelResult {
  // Helper class to interpret and render results from the server
  constructor(term, results) {
    this.term = term;
    this.results = results;
  }

  item(result, idx) {
    let subtitle = '';

    const classes = ['ticket-result'];

    if (idx === 0) classes.push('selected');

    if (result.subtitle !== undefined) {
      subtitle = `<span>${result.subtitle}</span>`;
    }

    const data = JSON.stringify(result.data);
    return `
      <div class='${classes.join(' ')}' data-data='${data}'}>
        ${result.title}
        ${subtitle}
      </div>
    `;
  }

  noResults() {
    return `
      <div class="alert alert-info" role="alert">
        No results for ${this.term}
      </div>
    `;
  }

  hasResults() {
    return this.term !== '' && this.results.length > 0;
  }

  render() {
    if (this.term === '') return '';
    if (this.results.length === 0) return this.noResults();

    return this.results.map(this.item).join('');
  }
}

const KEYMAPPINGS = {
  13: 'enter',
  27: 'esc',
  32: 'space',
  37: 'left',
  38: 'up',
  39: 'right',
  40: 'down',
};

class KeyDelegator {
  // Simple class to allow delegating key presses to functions on a target
  constructor(target, keyset) {
    this.target = target;
    this.keyset = keyset;
    target.on('keydown', this.onKeydown.bind(this));
  }

  onKeydown(event) {
    const name = KEYMAPPINGS[event.keyCode];
    if (this.keyset.includes(name)) {
      const f = this.target[`onKey${this.capitalize(name)}`];
      if (typeof f === 'function') {
        f.call(this.target, event);
      }
    }
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

class ChannelLinking extends Components.Base {
  initialize() {
    this.search = this.debounce(this.startSearch, 300);
  }

  bind() {
    this.cacheReferences();
    this.on('show.bs.modal', this.showingModal.bind(this));
    this.on('hidden.bs.modal', this.cleanUpModal.bind(this));

    // Focus keeps getting stolen so this hack keeps it
    this.on('shown.bs.modal', () => {
      this.$el.find('[autofocus]').focus();
    });

    this.on('keyup', '#ticket-search', evt => {
      this.searchTermUpdated(evt.target.value);
    });

    this.on('click', '.ticket-result', e => this.selectTicket(e.currentTarget));
    this.on('click', '#link-action', this.submitLink.bind(this));

    this.keyDelegator = new KeyDelegator(this, ['enter', 'up', 'down']);
  }

  onKeyEnter() {
    const selected = this.$el.find('.selected');
    if (selected.length > 0) this.submitLink();
  }

  onKeyUp() {
    const result = this.$el.find('.selected');
    const prev = result.prev();
    if (prev.length > 0) {
      $([result, prev]).toggleClass('selected');
    }
  }

  onKeyDown() {
    const result = this.$el.find('.selected');
    const next = result.next();
    if (next.length > 0) {
      $([result, next]).toggleClass('selected');
    }
  }

  startSearch(term) {
    this.cancelRequest();

    if (term === '') {
      this.searchSuccess(term, []);
      return;
    }

    this.$el.addClass('searching');

    this.xhr = $.ajax({
      url: this.getPath('ticket_search'),
      data: { term },
      success: res => this.searchSuccess(term, res),
      error: (res, reason) => {
        if (reason !== 'abort') {
          this.searchError(term, res.responseJSON);
        }
      },
      dataType: 'json',
    });
  }

  searchSuccess(term, response) {
    const result = new ChannelResult(term, response);
    this.$linkAction.attr('disabled', !result.hasResults());
    this.$results.html(result.render());
    this.$el.removeClass('searching');
  }

  searchError(term, response) {
    this.$results.html(`
      <div class="alert alert-danger" role="alert">
        ${response.error}
      </div>
    `);
    this.$linkAction.attr('disabled', true);
    this.$el.removeClass('searching');
  }

  cancelRequest() {
    if (this.requestIsPending()) {
      this.xhr.abort();
    }
  }

  requestIsPending() {
    return this.xhr && this.xhr.readyState !== 4;
  }

  showingModal(event) {
    this.sourceData = event.relatedTarget.dataset;
    this.$sourceChannel = $(event.relatedTarget).parents('.c-channel-action');
    this.$subtitle.text(`(${this.sourceData.channelDescription})`);
    this.$issueTypes.text(this.sourceData.ticketName);
    this.$searchLabel.html(this.sourceData.searchLabel);
  }

  cleanUpModal() {
    this.$results.html('');
    this.$search.val('');
    this.prevSearchTerm = '';
    this.$linkAction.attr('disabled', true);
    this.cancelRequest();
    this.$submitVerb.html('Link');
    this.$el.removeClass('searching');
  }

  searchTermUpdated(term) {
    if (this.prevSearchTerm !== term) {
      this.prevSearchTerm = term;
      this.search(term);
    }
  }

  selectTicket(target) {
    this.$el.find('.ticket-result').removeClass('selected');
    $(target).addClass('selected');
  }

  submitLink() {
    this.$submitVerb.html('<i class="fa fa-cog fa-spin"></i> Linking');
    const selectedData = this.$el.find('.selected').data('data');

    this.xhr = $.ajax({
      url: this.getPath('link'),
      method: 'POST',
      data: {
        fault_id: this.sourceData.faultId,
        data: selectedData,
      },
      dataType: 'html',
      success: response => {
        // ujs element replacement
        this.$sourceChannel.html(response);
        this.$el.modal('hide');
      },
      error: () => {
        this.$submitVerb.html('Link');
      },
    });
  }

  cacheReferences() {
    this.$results = this.$el.find('.form-results');
    this.$linkAction = this.$el.find('#link-action');
    this.$search = this.$el.find('#ticket-search');
    this.$issueTypes = this.$el.find('.modal-issue-type');
    this.$subtitle = this.$el.find('.modal-issue-subtitle');
    this.$submitVerb = this.$el.find('span.verb');
    this.$searchLabel = this.$el.find('.search-label');
  }

  // Utils
  //
  getPath(action) {
    return `${this.sourceData.path}/${action}`;
  }

  debounce(func, wait, immediate) {
    let timeout;
    return function(...args) {
      const context = this;
      const later = function() {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  }
}

Components.collection.register({
  selector: '.c-channel-link-modal',
  klass: ChannelLinking,
});
